/**
 * Help Center Content
 * Contains changelog and how-to guides for Paws on Map
 */

export interface ChangelogEntry {
  version: string
  date: string
  title: string
  changes: {
    type: 'feature' | 'fix' | 'improvement'
    text: string
  }[]
}

export interface Guide {
  id: string
  title: string
  icon: string
  steps: string[]
}

export interface HelpContent {
  version: string
  lastUpdated: string
  changelog: ChangelogEntry[]
  guides: {
    admin: Guide[]
    user: Guide[]
  }
}

export const HELP_CONTENT: HelpContent = {
  version: '3.0.0',
  lastUpdated: '2025-12-14',

  changelog: [
    {
      version: '3.0.0',
      date: '2025-12-14',
      title: 'Modern Architecture Refactor',
      changes: [
        { type: 'improvement', text: 'Complete codebase modernization with TypeScript' },
        { type: 'improvement', text: 'Vite build system for faster development' },
        { type: 'improvement', text: 'Tailwind CSS for consistent styling' },
        { type: 'improvement', text: 'Alpine.js reactive components' },
        { type: 'feature', text: 'Improved map performance with marker clustering' },
        { type: 'fix', text: 'Better location display from address parts' },
      ],
    },
    {
      version: '2.1.0',
      date: '2025-12-14',
      title: 'Mobile Camera & Bug Fixes',
      changes: [
        { type: 'feature', text: 'Direct camera capture on mobile devices' },
        { type: 'fix', text: 'Report approval system now works correctly' },
        { type: 'fix', text: 'Mobile footer no longer takes excessive space' },
        { type: 'fix', text: 'Cache-busting for JavaScript files' },
      ],
    },
    {
      version: '2.0.0',
      date: '2025-12-14',
      title: 'Report Approval System & CI/CD',
      changes: [
        { type: 'feature', text: 'Admin approval workflow for new reports' },
        { type: 'feature', text: 'Pending Approval view with approve/reject actions' },
        { type: 'feature', text: 'Production branch deployment strategy' },
        { type: 'improvement', text: 'Reports now require admin review before going public' },
      ],
    },
    {
      version: '1.3.0',
      date: '2025-12-14',
      title: 'Interactive Maps',
      changes: [
        { type: 'feature', text: 'Map view for Geographic Search in Admin' },
        { type: 'feature', text: 'Map/Grid toggle for Find Animals page' },
        { type: 'feature', text: 'Clustered markers for better performance' },
        { type: 'improvement', text: 'Improved map loading and interactions' },
      ],
    },
    {
      version: '1.2.0',
      date: '2025-12-13',
      title: 'Geographic Filtering',
      changes: [
        { type: 'feature', text: 'Country and city-based filtering' },
        { type: 'feature', text: 'Automatic location detection' },
        { type: 'feature', text: 'Geographic metadata on reports' },
      ],
    },
    {
      version: '1.1.0',
      date: '2025-12-12',
      title: 'Admin Dashboard Improvements',
      changes: [
        { type: 'feature', text: 'CSV export for reports' },
        { type: 'feature', text: 'Advanced filtering options' },
        { type: 'improvement', text: 'Unified admin and adopters views' },
      ],
    },
    {
      version: '1.0.0',
      date: '2025-12-10',
      title: 'Initial Release',
      changes: [
        { type: 'feature', text: 'Report stray animals with photos' },
        { type: 'feature', text: 'Find animals near your location' },
        { type: 'feature', text: 'Admin dashboard for management' },
        { type: 'feature', text: 'Google Sign-In for admins' },
      ],
    },
  ],

  guides: {
    admin: [
      {
        id: 'pending-approval',
        title: 'Approving Reports',
        icon: 'i-pending-actions',
        steps: [
          "Click 'Pending Approval' in the sidebar",
          "Review each report's photo, location, and details",
          "Click the green 'Approve' button to make it public",
          "Click the red 'Reject' button to decline (add optional reason)",
        ],
      },
      {
        id: 'manage-reports',
        title: 'Managing Reports',
        icon: 'i-dashboard',
        steps: [
          "Click 'Manage Reports' in the sidebar",
          'Use filters to find specific reports by status, type, or date',
          'Click a report card to view full details',
          'Update status or add notes as needed',
          "Use 'Export CSV' to download report data",
        ],
      },
      {
        id: 'geographic-search',
        title: 'Geographic Search',
        icon: 'i-map',
        steps: [
          "Click 'Geographic' in the sidebar",
          'Allow location access or enter a location manually',
          'Adjust the search radius using the slider',
          'Toggle between Map and Grid view',
          'Click markers or cards to view report details',
        ],
      },
      {
        id: 'update-status',
        title: 'Updating Report Status',
        icon: 'i-edit-note',
        steps: [
          'Find the report in Manage Reports',
          'Click the report card to open details',
          'Select new status from the dropdown',
          'Status options: New, Investigating, Resolved, Closed',
        ],
      },
    ],
    user: [
      {
        id: 'report-animal',
        title: 'How to Report an Animal',
        icon: 'i-add',
        steps: [
          "Tap 'Take a Photo' or select from gallery",
          'Allow location access (or enter address manually)',
          'Select the animal type (Dog, Cat, Bird, Other)',
          'Choose the condition (Healthy, Injured, etc.)',
          'Add a description with helpful details',
          "Tap 'Submit Report' and save your Case ID",
        ],
      },
      {
        id: 'find-animals',
        title: 'Finding Animals Nearby',
        icon: 'i-search',
        steps: [
          "Go to 'Find Animals' page",
          'Allow location access for nearby results',
          'Use filters to narrow by animal type or status',
          'Toggle between Grid and Map view',
          'Click a card/marker to see full details',
        ],
      },
      {
        id: 'track-report',
        title: 'Tracking Your Report',
        icon: 'i-schedule',
        steps: [
          'Save the Case ID shown after submission',
          'Use the search feature to find by Case ID',
          'Check the status: Pending, New, Investigating, Resolved',
        ],
      },
      {
        id: 'what-happens-next',
        title: 'What Happens After Reporting',
        icon: 'i-info',
        steps: [
          'Your report goes to admin for approval',
          'Once approved, it appears on the public Find page',
          'Admins may update status as they investigate',
          'You can check progress using your Case ID',
        ],
      },
    ],
  },
}

/**
 * Get the last seen help version from localStorage
 */
export function getLastSeenVersion(): string | null {
  return localStorage.getItem('lastSeenHelpVersion')
}

/**
 * Set the last seen help version in localStorage
 */
export function setLastSeenVersion(version: string): void {
  localStorage.setItem('lastSeenHelpVersion', version)
}

/**
 * Check if there are new updates since last seen
 */
export function hasNewUpdates(): boolean {
  const lastSeen = getLastSeenVersion()
  return lastSeen !== HELP_CONTENT.version
}

/**
 * Get change type badge class
 *
 * Returns POM low-poly status pill classes (mono-caps shape with the spec
 * five-color combos).
 */
export function getChangeTypeBadgeClass(type: string): string {
  switch (type) {
    case 'feature':
      return 'pill pill-mint'
    case 'fix':
      return 'pill pill-coral'
    case 'improvement':
      return 'pill pill-mustard'
    default:
      return 'pill pill-closed'
  }
}
